import { useEffect } from 'react';
import type {
  OperatorUserPlatformIds,
  ShipperUserPlatformIds,
} from 'src/apis/monolith/types';
import { useAxios } from 'src/hooks/use-axios';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';

type Params = {
  /**
   * If you don't pass a uuid, it will return the data of the currently logged user.
   */
  userUuid?: string;
};

/**
 * This returns all relevant old user IDs for a given user.
 * This works also for users that don't have a legacy user role!
 */
export function useOldUserId({ userUuid }: Params = {}) {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const cognitoUser = useCognitoUser();

  const oldUserId = useAxios(
    (axios, baseConfig, { userUuid }: Params) =>
      axios.request<ShipperUserPlatformIds | OperatorUserPlatformIds>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.monolithApiUrl}/user-account/platform-ids`,
        params: { 'user-uuid': userUuid },
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (cognitoUser && !monolithUser) return;
    oldUserId.execute({ userUuid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userUuid]);

  if (cognitoUser && !monolithUser) return;

  return oldUserId;
}

export type OldUserIdRequest = ReturnType<typeof useOldUserId>;
